<template>
  <div class="client-hub-tenant">
    <cs-button primary label="New Hub Tenant" @click="onNewHubTenantClick()" />

    <cs-form-dialog
      id="dlg-new-hub-tenant"
      v-model="showNewHubTenantDialog"
      body-id="dlg-new-hub-tenant-body"
      heading="New Hub Tenant"
      :primary-action="{
        label: primaryLabel,
        eventName: 'primary-click',
        loading: saveActionInProgress,
        disabled: saveButtonDisabled
      }"
      :secondary-action1="{
        label: 'Cancel',
        eventName: 'secondary1-click',
        disabled: saveActionInProgress
      }"
      @primary-click="onPrimaryClick()"
      @secondary1-click="onCancelClick()"
    >
      <template #cs-form-dialog-content>
        <v-container class="pa-1">
          <v-row no-gutters>
            <v-col cols="12">
              <div class="upload-instruction">
                Upload the migration file to register one Hub tenant per row.
              </div>
              <div class="d-flex align-center csv-label">
                <span>migration_tenant_auth_info.csv</span>
                <stb-icon-tooltip id="client-hub-tenant-csv-help" location="top" class="ml-1">
                  <div class="csv-help-tooltip">
                    Upload the migration_tenant_auth_info.csv returned by the Hub Migration tool.
                    Each row becomes one Hub tenant; tenants that already exist (matched by Tenant
                    Sub Domain) are skipped.
                  </div>
                </stb-icon-tooltip>
                <file-upload
                  id="client-hub-tenant-file-upload"
                  title=""
                  file-extensions=".csv"
                  :loading="uploadFile.loading"
                  @file-changed="onFileChanged"
                >
                  <template #custom-info>
                    <div class="status-container">
                      <v-icon v-if="uploadFile.status === 'success'" color="rgb(23, 188, 22)">
                        mdi-check-circle-outline
                      </v-icon>
                      <v-icon
                        v-else-if="uploadFile.status === 'error'"
                        color="error"
                        @click="showErrorDetail()"
                        >mdi-alert-circle-outline
                      </v-icon>
                    </div>
                  </template>
                </file-upload>
              </div>
            </v-col>
          </v-row>

          <v-card v-if="saveSummary" elevation="2" border class="mt-4 mb-6 pa-4">
            <v-card-subtitle><span class="summary-title">Upload complete</span></v-card-subtitle>
            <v-card-text>
              <div class="summary-line">
                <span class="summary-count summary-count-created">
                  {{ saveSummary.created.length }}
                </span>
                <span class="summary-line-text"
                  >{{ saveSummary.created.length === 1 ? 'tenant' : 'tenants' }} created,</span
                >
                <span class="summary-count summary-count-skipped">
                  {{ saveSummary.skipped.length }}
                </span>
                <span class="summary-line-text"
                  >already existed and
                  {{ saveSummary.skipped.length === 1 ? 'was' : 'were' }} skipped.</span
                >
              </div>
              <div v-if="saveSummary.skipped.length" class="summary-scroll mt-2">
                <div class="summary-list">
                  <div class="summary-group-label">Already exists — will skip</div>
                  <div
                    v-for="tenant in saveSummary.skipped"
                    :key="tenant.organisationId"
                    class="summary-row summary-row-skip"
                  >
                    <span class="summary-row-name">{{ tenant.displayName }}</span>
                    <span class="summary-row-id">{{ tenant.organisationId }}</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card v-else-if="errorUploadDisplayed" elevation="2" border class="mt-4 mb-6 pa-4">
            <v-card-text class="error-section">
              <div v-if="hasCsvValidationErrors" class="error-section-title">
                Invalid CSV file content
              </div>
              <v-card
                v-for="(error, index) in errorFile.errors"
                :key="error.row || error.description || index"
                class="client-hub-tenant-error-card client-hub-tenant-error-card--emphasis"
                variant="outlined"
              >
                <v-card-text class="error-card-content">
                  <div class="error-message">{{ error.error }}</div>
                  <div v-if="error.description" class="error-description">
                    {{ error.description }}
                  </div>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>

          <v-card v-else-if="previewSummary" elevation="2" border class="mt-4 mb-6 pa-4">
            <v-card-subtitle><span class="summary-title">CSV upload preview</span></v-card-subtitle>
            <v-card-text>
              <div class="summary-line">
                <span class="summary-count summary-count-created">
                  {{ previewSummary.created.length }}
                </span>
                <span class="summary-line-text">to create &middot;</span>
                <span class="summary-count summary-count-skipped">
                  {{ previewSummary.skipped.length }}
                </span>
                <span class="summary-line-text">already exist (will skip)</span>
              </div>
              <div class="summary-scroll">
                <div class="summary-list">
                  <div class="summary-group-label">To create</div>
                  <div v-if="previewSummary.created.length === 0" class="summary-none">
                    No new tenants in this file.
                  </div>
                  <div
                    v-for="tenant in previewSummary.created"
                    :key="tenant.organisationId"
                    class="summary-row"
                  >
                    <span class="summary-row-name">{{ tenant.displayName }}</span>
                    <span class="summary-row-id">{{ tenant.organisationId }}</span>
                  </div>
                  <div class="summary-group-label">Already exists — will skip</div>
                  <div v-if="previewSummary.skipped.length === 0" class="summary-none">None.</div>
                  <div
                    v-for="tenant in previewSummary.skipped"
                    :key="tenant.organisationId"
                    class="summary-row summary-row-skip"
                  >
                    <span class="summary-row-name">{{ tenant.displayName }}</span>
                    <span class="summary-row-id">{{ tenant.organisationId }}</span>
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>

          <v-card v-else border class="mt-4 mb-6 pa-4 client-hub-tenant-empty">
            <v-card-text class="text-center">
              Click Upload button above to choose your migration_tenant_auth_info.csv. You will see
              a preview of the tenants before saving.
            </v-card-text>
          </v-card>
        </v-container>
      </template>
    </cs-form-dialog>
  </div>
</template>

<script>
import { CSBase } from '@complispace/cs-design-system';
import * as types from '../store/mutationTypes';
import FileUpload from './FileUpload';
import StbIconTooltip from './StbIconTooltip';

export default {
  name: 'ClientHubTenant',

  components: {
    FileUpload,
    StbIconTooltip
  },

  extends: CSBase,

  props: {
    clientSid: { type: String, required: false, default: '' }
  },
  emits: ['tenants-uploaded'],

  data() {
    return {
      showNewHubTenantDialog: false,
      uploadFile: {
        status: null,
        loading: false,
        errors: [],
        file: null
      },
      previewSummary: null,
      saveSummary: null,
      errorUploadDisplayed: false,
      errorFile: {},
      saveActionInProgress: false
    };
  },

  computed: {
    primaryLabel() {
      if (this.saveSummary) return 'Done';
      const createCount = this.previewSummary?.created.length || 0;
      if (createCount > 0) {
        return `Create ${createCount} ${createCount === 1 ? 'tenant' : 'tenants'}`;
      }
      if (this.previewSummary) return 'Nothing to create';
      return 'Save';
    },
    saveButtonDisabled() {
      if (this.saveSummary) return false;
      if (this.saveActionInProgress) return true;
      return !(this.previewSummary && this.previewSummary.created.length > 0);
    },
    hasCsvValidationErrors() {
      return (
        Array.isArray(this.errorFile.errors) &&
        this.errorFile.errors.some((error) => Boolean(error.description))
      );
    }
  },

  methods: {
    onNewHubTenantClick() {
      this.resetUploadFile();
      this.showNewHubTenantDialog = true;
    },
    async onFileChanged(id, fileInfo) {
      this.uploadFile.loading = true;
      this.uploadFile.file = fileInfo.file;
      this.previewSummary = null;
      this.saveSummary = null;
      this.errorUploadDisplayed = false;
      this.errorFile = {};
      try {
        const patchRes = await this.$store.dispatch('hub-integration/postAuthInfoUpload', {
          attachment: fileInfo.file,
          saveMode: 'preview'
        });
        if (patchRes.error) {
          this.handleUploadFileError(patchRes.error);
          return;
        }
        this.uploadFile.status = 'success';
        this.previewSummary = {
          created: patchRes.created || [],
          skipped: patchRes.skipped || []
        };
        this.errorUploadDisplayed = false;
        this.errorFile = {};
      } catch (error) {
        this.handleUploadFileError(error);
      } finally {
        this.uploadFile.loading = false;
      }
    },
    handleUploadFileError(error) {
      this.uploadFile.status = 'error';
      this.previewSummary = null;
      this.saveSummary = null;
      if (error.statusCode === 422) {
        this.$store.commit(types.COMMON_SET_IS_RESPONSE_WITH_ERROR, false);
        this.uploadFile.errors = error.contentErrors;
      } else {
        this.uploadFile.errors = [{ error: error.error || error.message, description: '' }];
      }
      this.errorUploadDisplayed = true;
      this.errorFile = { ...this.uploadFile, errors: [...this.uploadFile.errors] };
    },
    showErrorDetail() {
      this.errorUploadDisplayed = true;
      this.errorFile = { ...this.uploadFile, errors: [...this.uploadFile.errors] };
    },
    onPrimaryClick() {
      if (this.saveSummary) {
        this.onCancelClick();
        return;
      }
      this.saveHubTenant();
    },
    async saveHubTenant() {
      if (!this.uploadFile.file) return;
      try {
        this.saveActionInProgress = true;
        this.errorUploadDisplayed = false;
        this.errorFile = {};
        const result = await this.$store.dispatch('hub-integration/postAuthInfoUpload', {
          attachment: this.uploadFile.file,
          saveMode: 'save'
        });
        if (result?.error) {
          this.handleUploadFileError(result.error);
          return;
        }
        const created = result.created || [];
        const skipped = result.skipped || [];
        this.saveSummary = { created, skipped };
        this.errorUploadDisplayed = false;
        this.errorFile = {};
        if (created.length > 0) {
          this.$emit('tenants-uploaded', {
            created: created.length,
            skipped: skipped.length
          });
        }
      } catch (error) {
        this.handleUploadFileError(error);
      } finally {
        this.saveActionInProgress = false;
      }
    },
    onCancelClick() {
      this.resetUploadFile();
      this.showNewHubTenantDialog = false;
    },
    resetUploadFile() {
      this.previewSummary = null;
      this.saveSummary = null;
      this.errorUploadDisplayed = false;
      this.errorFile = {};
      this.uploadFile.status = null;
      this.uploadFile.loading = false;
      this.uploadFile.errors = [];
      this.uploadFile.file = null;
    }
  }
};
</script>

<style scoped>
.upload-instruction {
  font-size: 16px;
  color: rgba(0, 0, 0, 0.76);
  margin-bottom: 8px;
}

.csv-label {
  font-size: 18px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.9);
}

.client-hub-tenant-error-card {
  margin-bottom: 12px;
}

.client-hub-tenant-error-card--emphasis {
  border-color: rgba(91, 33, 33, 0.35) !important;
  background-color: rgba(152, 28, 28, 0.05);
}

.error-section-title {
  color: #7a1e1e;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.15;
  margin-bottom: 12px;
}

.error-card-content {
  padding: 16px 18px !important;
}

.summary-title {
  font-size: 15px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.52);
}

.summary-line {
  margin-bottom: 12px;
  font-size: 20px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.86);
}

.summary-count {
  font-size: 42px;
  font-weight: 700;
  line-height: 1;
}

.summary-count-created {
  color: #1b7f4d;
}

.summary-count-skipped {
  color: #99600a;
}

.summary-line-text {
  margin: 0 6px;
}

.summary-group-label {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(0, 0, 0, 0.56);
  margin: 16px 0 6px;
}

.summary-scroll {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.summary-list {
  min-width: 720px;
  width: max-content;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  gap: 32px;
  padding: 9px 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  min-width: 100%;
  white-space: nowrap;
}

.summary-row-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.86);
}

.summary-row-id {
  font-size: 14px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.5);
  white-space: nowrap;
}

.summary-row-skip {
  color: rgba(0, 0, 0, 0.6);
}

.summary-none {
  color: rgba(0, 0, 0, 0.6);
  padding: 6px 0;
}

.error-message {
  color: #5b2020;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.error-description {
  color: rgba(80, 37, 37, 0.9);
  font-size: 13px;
  margin-top: 4px;
}

.client-hub-tenant-empty {
  color: rgba(0, 0, 0, 0.6);
}
</style>

<style>
/* Unscoped because Vuetify teleports v-menu content outside this SFC. Targets the parent
   tooltip container only when it wraps this dialog's marker, so no other tooltip is affected. */
.stb-icon-tooltip-content:has(.csv-help-tooltip) {
  background-color: #ffffff !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}
</style>
